/**
 * P3-S1 릴레이 기계(SR-MODE-07 · FIELD-DEC-003) — 셸 쪽 순수 함수(화면·시험 공용).
 *  - FS-08-01 순서 배치 초안: 구간 = 학생 한 명(기계 하나) + 들어오는 것(in) · 내보내는 것(out) · 타임아웃(선택)
 *    · 구간 수 = 배치한 학생 수(2~30) · 한 학생 한 구간 · 순서를 바꾸면 카드(학생 + 경계 종류)가 함께 옮겨 간다
 *    (구간 코드는 그 학생이 자기 경계에 맞춰 짠다 — FS-08-02)
 *  - 정합 검사는 `@picnic-field/modes` 의 checkRelayChain 을 그대로 쓴다(룸 서버 · 제어 평면과 같은 규칙) — 화면은 늦게 불러온다
 *  - 학생 룸 화면(최소 안내): FullState meta.mode(kind relay) + ev relayStage 로 지금 구간 · 내 구간 상태를 따라간다
 * 번호는 0 부터(화면은 +1) — 룸 서버 RelayMachine 과 같다. 점수·순위 없음(DR-01) · 실패 이유는 당사자에게만.
 */
import type {
  RelayIssue,
  RelayRules,
  RelayStagePlan,
  RelayStageStatus,
  RelayPhase,
} from "@picnic-field/modes";
import type {
  Connection,
  Trigger,
  RelayDefInfo,
  RelayPlanItem,
  RelayStudent,
  RelayRenderStatus,
  RelayRender,
  RelayInfo,
  RelayDraftStage,
  RelayDraft,
  RelayFail,
  RelayStageInfo,
  RelayMine,
  RelayTimers,
  RelayView,
  StudentRelayState,
  RelayActions,
  RelayNowKind,
  RelayNoticeKind,
} from "../types";

/** 룸 권위 리플레이 재생 플래그 — 원본은 constants/flags.ts(영상 기능도 본다) */
export { ROOM_REPLAY_PLAYER } from "../../../constants/flags";

export const TRIGGERS: readonly Trigger[] = ["ball", "lever", "zone"];
/** 경계 종류 아이콘(SCR v0.7 검토 1-5 — 공 · 레버 · 존 세 개로 통일) */
export const TRIGGER_ICON: Record<Trigger, string> = { ball: "⚪", lever: "🎚", zone: "🟩" };

export const RELAY_DEF_DEFAULT: RelayDefInfo = {
  maxStages: 30,
  minStages: 2,
  triggers: [...TRIGGERS],
  stageTimeoutS: 30,
  timeoutChoicesS: [],
};

const obj = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === "string" ? v : null);
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

export const isTrigger = (v: unknown): v is Trigger =>
  typeof v === "string" && (TRIGGERS as readonly string[]).includes(v);
const STATUSES: readonly RelayStageStatus[] = [
  "waiting",
  "armed",
  "running",
  "done",
  "failed",
  "skipped",
];
const readStatus = (v: unknown): RelayStageStatus =>
  typeof v === "string" && (STATUSES as readonly string[]).includes(v)
    ? (v as RelayStageStatus)
    : "waiting";
const readPhase = (v: unknown): RelayPhase => (v === "running" || v === "allDone" ? v : "setup");
const CONNECTIONS: readonly Connection[] = ["online", "dropped", "offline", "isolated"];

function readDef(v: unknown): RelayDefInfo {
  const o = obj(v) ?? {};
  const triggers = arr(o.triggers).filter(isTrigger);
  const choices = arr(o.timeoutChoicesS)
    .map(num)
    .filter((n): n is number => n !== null && n > 0);
  return {
    maxStages: Math.min(30, num(o.maxStages) ?? RELAY_DEF_DEFAULT.maxStages),
    minStages: Math.max(2, num(o.minStages) ?? RELAY_DEF_DEFAULT.minStages),
    triggers: triggers.length ? triggers : [...TRIGGERS],
    stageTimeoutS: num(o.stageTimeoutS) ?? RELAY_DEF_DEFAULT.stageTimeoutS,
    timeoutChoicesS: choices,
  };
}

/** GET …/teacher/room/relay (계약 §3) — 모르는 값은 안전한 기본으로 */
export function readRelay(v: unknown): RelayInfo {
  const o = obj(v) ?? {};
  const plan = arr(o.plan)
    .map(obj)
    .filter((p): p is Record<string, unknown> => !!p && typeof p.pid === "string")
    .map((p, i): RelayPlanItem => {
      const w = obj(p.work);
      return {
        stage: num(p.stage) ?? i,
        pid: String(p.pid),
        nickname: str(p.nickname) ?? "—",
        machine: num(p.machine),
        work: w ? { version: num(w.version) ?? 1, title: str(w.title) ?? "" } : null,
        in: isTrigger(p.in) ? p.in : "ball",
        out: isTrigger(p.out) ? p.out : "ball",
        timeoutS: num(p.timeoutS),
      };
    })
    .sort((a, b) => a.stage - b.stage);
  return {
    enabled: o.enabled === true,
    def: readDef(o.def),
    plan,
    state: readPhase(o.state ?? o.phase),
    current: num(o.current),
    paused: o.paused === true,
    stages: arr(o.stages)
      .map(obj)
      .filter((s): s is Record<string, unknown> => !!s)
      .map((s, i) => ({
        stage: num(s.stage) ?? i,
        status: readStatus(s.status),
        attempt: num(s.attempt) ?? 0,
        remainingS: num(s.remainingS),
        ready: s.ready === true,
        lastFail: s.lastFail === "timeout" || s.lastFail === "lost" ? s.lastFail : null,
      })),
    students: arr(o.students)
      .map(obj)
      .filter((s): s is Record<string, unknown> => !!s && typeof s.pid === "string")
      .map((s) => {
        const c = str(s.connection);
        // 2026-09-30 — 현재 스냅샷(있으면 · 교사 카드 ⚠ 릴레이 준비 경고가 run-bundle 로 판정한다)
        const sid = str(s.snapshotId);
        return {
          pid: String(s.pid),
          nickname: str(s.nickname) ?? "—",
          machine: num(s.machine),
          connection:
            c && (CONNECTIONS as readonly string[]).includes(c) ? (c as Connection) : "offline",
          ...(sid ? { snapshotId: sid } : {}),
        };
      }),
    replayId: str(o.replayId),
    render: readRender(o.render),
  };
}

const RENDER_STATUSES: readonly RelayRenderStatus[] = [
  "queued",
  "rendering",
  "retrying",
  "done",
  "failed",
  "dead",
];
function readRender(v: unknown): RelayRender | null {
  const o = obj(v);
  if (!o) return null;
  const raw = str(o.status);
  const st = raw === "running" ? "rendering" : raw;
  return {
    status:
      st && (RENDER_STATUSES as readonly string[]).includes(st)
        ? (st as RelayRenderStatus)
        : "queued",
    attempts: num(o.attempts) ?? 0,
  };
}

// ── FS-08-01 배치 초안 ─────────────────────────────────────────────────────

export const draftOf = (plan: readonly RelayPlanItem[]): RelayDraft => ({
  stages: plan.map((p) => ({ pid: p.pid, in: p.in, out: p.out, timeoutS: p.timeoutS })),
});

export const sameDraft = (a: RelayDraft, b: RelayDraft) =>
  a.stages.length === b.stages.length &&
  a.stages.every((s, i) => {
    const o = b.stages[i]!;
    return s.pid === o.pid && s.in === o.in && s.out === o.out && s.timeoutS === o.timeoutS;
  });

/** 배치 후보 = 기계가 있는 학생(시작 전 끊김·미접속 학생도 배치할 수 있다 — 교사 판단 · DEC-003 §6) */
export const isCandidate = (s: RelayStudent) => s.machine !== null;

/** 미배치(배치 후보인데 구간이 없는 학생) — 이름순 */
export function unplaced(d: RelayDraft, students: readonly RelayStudent[]): RelayStudent[] {
  const inPlan = new Set(d.stages.map((s) => s.pid));
  return students
    .filter((s) => isCandidate(s) && !inPlan.has(s.pid))
    .sort((a, b) => a.nickname.localeCompare(b.nickname, "ko") || a.pid.localeCompare(b.pid));
}

/**
 * pid 를 구간 at(0 부터 · 뒤 구간은 한 칸씩 밀린다)에 넣는다. 이미 배치된 학생이면 그 카드를 옮긴다.
 * 새 카드의 경계는 앞뒤와 맞게 — 들어오는 것 = 앞 구간이 내보내는 것 · 내보내는 것 = 뒤 구간이 들어오는 것.
 * 상한(maxStages)이면 그대로.
 */
export function place(
  d: RelayDraft,
  pid: string,
  at: number,
  max = 30,
  first: Trigger = "ball",
): RelayDraft {
  const from = d.stages.findIndex((s) => s.pid === pid);
  if (from >= 0) return moveStage(d, from, at);
  if (d.stages.length >= max) return d;
  const i = Math.max(0, Math.min(at, d.stages.length));
  const prev = d.stages[i - 1];
  const next = d.stages[i];
  const inK = prev?.out ?? next?.in ?? first;
  const card: RelayDraftStage = { pid, in: inK, out: next?.in ?? inK, timeoutS: null };
  const stages = [...d.stages];
  stages.splice(i, 0, card);
  return { stages };
}

/** 카드 옮기기(from → to · 둘 다 0 부터) */
export function moveStage(d: RelayDraft, from: number, to: number): RelayDraft {
  if (from < 0 || from >= d.stages.length) return d;
  const t = Math.max(0, Math.min(to, d.stages.length - 1));
  if (t === from) return d;
  const stages = [...d.stages];
  const [card] = stages.splice(from, 1);
  stages.splice(t, 0, card!);
  return { stages };
}

/** 배치에서 빼기(미배치로) */
export function remove(d: RelayDraft, pid: string): RelayDraft {
  const stages = d.stages.filter((s) => s.pid !== pid);
  return stages.length === d.stages.length ? d : { stages };
}

export function setKind(d: RelayDraft, i: number, side: "in" | "out", k: Trigger): RelayDraft {
  const s = d.stages[i];
  if (!s || s[side] === k) return d;
  const stages = [...d.stages];
  stages[i] = { ...s, [side]: k };
  return { stages };
}

export function setTimeoutS(d: RelayDraft, i: number, sec: number | null): RelayDraft {
  const s = d.stages[i];
  if (!s || s.timeoutS === sec) return d;
  const stages = [...d.stages];
  stages[i] = { ...s, timeoutS: sec };
  return { stages };
}

/** 구간 i 의 들어오는 것을 앞 구간이 내보내는 것으로 맞춘다(불일치 배너 '맞추기') */
export function alignIn(d: RelayDraft, i: number): RelayDraft {
  const prev = d.stages[i - 1];
  return prev ? setKind(d, i, "in", prev.out) : d;
}

/**
 * 🎲 무작위 — 배치 후보(연결 정상 · 기계 있음) 전원을 섞어 순서를 정한다(상한까지). 각 카드의 내보내는 것은
 * 그대로 두고(없던 학생은 첫 종류), 들어오는 것은 앞 구간에 맞춰 이어 준다 — 결과는 늘 정합.
 */
export function randomize(
  d: RelayDraft,
  students: readonly RelayStudent[],
  opts: { max?: number; triggers?: readonly Trigger[]; rnd?: () => number } = {},
): RelayDraft {
  const max = opts.max ?? 30;
  const kinds = opts.triggers?.length ? opts.triggers : TRIGGERS;
  const rnd = opts.rnd ?? Math.random;
  const pool = students
    .filter((s) => isCandidate(s) && s.connection === "online")
    .map((s) => s.pid);
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [pool[i], pool[j]] = [pool[j]!, pool[i]!];
  }
  const old = new Map(d.stages.map((s) => [s.pid, s]));
  const stages: RelayDraftStage[] = [];
  for (const pid of pool.slice(0, max)) {
    const was = old.get(pid);
    const prev = stages[stages.length - 1];
    const out = was?.out ?? kinds[Math.floor(rnd() * kinds.length)] ?? "ball";
    stages.push({
      pid,
      in: prev?.out ?? was?.in ?? out,
      out,
      timeoutS: was?.timeoutS ?? null,
    });
  }
  return { stages };
}

/** 정합 검사용 배치(기계 번호 — 기계를 모르는 학생은 겹치지 않게 음수) */
export function toStagePlans(d: RelayDraft, students: readonly RelayStudent[]): RelayStagePlan[] {
  const machine = new Map(students.map((s) => [s.pid, s.machine]));
  return d.stages.map((s, i) => ({
    machine: machine.get(s.pid) ?? -1 - i,
    in: s.in,
    out: s.out,
    ...(s.timeoutS !== null ? { timeoutS: s.timeoutS } : {}),
  }));
}

export const rulesOf = (def: RelayDefInfo): RelayRules => ({
  minStages: def.minStages,
  maxStages: def.maxStages,
  triggers: def.triggers,
  ...(def.timeoutChoicesS.length ? { timeoutChoicesS: def.timeoutChoicesS } : {}),
});

/** PUT …/relay/plan 본문 */
export const payload = (d: RelayDraft) => ({
  stages: d.stages.map((s) => ({
    pid: s.pid,
    in: s.in,
    out: s.out,
    ...(s.timeoutS !== null ? { timeoutS: s.timeoutS } : {}),
  })),
});

/** 불일치 구간 번호(0 부터) — 연결선을 빨갛게 그릴 카드 */
export const mismatchStages = (issues: readonly RelayIssue[]) =>
  new Set(issues.filter((x) => x.code === "mismatch" && x.stage !== null).map((x) => x.stage!));

/** 422 { field:"plan", issues } 의 issues 를 읽는다 */
export function readIssues(v: unknown): RelayIssue[] {
  return arr(v)
    .map(obj)
    .filter((x): x is Record<string, unknown> => !!x && typeof x.code === "string")
    .map((x) => ({
      stage: num(x.stage),
      code: x.code as RelayIssue["code"],
      message: str(x.message) ?? "",
    }));
}

// ── 학생 룸 화면(FS-08-02 내 구간 · FS-08-03 실행 · 칠판 FS-12-01 릴레이 변형) ──────────────

const readFail = (v: unknown): RelayFail | null => (v === "timeout" || v === "lost" ? v : null);

/** 자동 재시도(DEC-003 §2-5) · Armed 창 — 이벤트로 카운트다운을 다시 세울 때 */
export const RELAY_AUTO_RETRY_S = 15;
export const RELAY_ARM_S = 0.5;

function readInfo(s: Record<string, unknown>): RelayStageInfo {
  return {
    status: readStatus(s.status),
    machine: num(s.machine),
    in: isTrigger(s.in) ? s.in : "ball",
    out: isTrigger(s.out) ? s.out : "ball",
    ready: s.ready === true,
    timeoutS: num(s.timeoutS),
  };
}

export function readTimers(v: unknown): RelayTimers {
  const o = obj(v) ?? {};
  const out: RelayTimers = {};
  for (const k of ["armLeftS", "runLeftS", "retryInS", "absentLeftS"] as const) {
    const n = num(o[k]);
    if (n !== null && n >= 0) out[k] = n;
  }
  return out;
}

/** 앞 · 뒤 구간(구간 정보에서) — meta.mine 에 prev/next 가 없을 때 */
function neighbours(info: readonly RelayStageInfo[], i: number) {
  const p = info[i - 1];
  const n = info[i + 1];
  return {
    prev: p ? { stage: i - 1, machine: p.machine, out: p.out } : null,
    next: n ? { stage: i + 1, machine: n.machine, in: n.in } : null,
  };
}

function readMine(
  v: unknown,
  info: readonly RelayStageInfo[],
  myStage: number | null,
): RelayMine | null {
  const o = obj(v);
  const stage = num(o?.stage) ?? myStage;
  if (stage === null || stage < 0 || stage >= info.length) return null;
  const s = info[stage]!;
  const nb = neighbours(info, stage);
  const p = obj(o?.prev);
  const n = obj(o?.next);
  return {
    stage,
    status: o && o.status !== undefined ? readStatus(o.status) : s.status,
    attempt: num(o?.attempt) ?? 0,
    ready: o ? o.ready === true : s.ready,
    lastFail: readFail(o?.lastFail),
    swapPending: o?.swapPending === true,
    prev: p
      ? {
          stage: num(p.stage) ?? stage - 1,
          machine: num(p.machine),
          out: isTrigger(p.out) ? p.out : (nb.prev?.out ?? "ball"),
        }
      : nb.prev,
    next: n
      ? {
          stage: num(n.stage) ?? stage + 1,
          machine: num(n.machine),
          in: isTrigger(n.in) ? n.in : (nb.next?.in ?? "ball"),
        }
      : nb.next,
  };
}

/**
 * FullState meta.mode(kind relay) — 룸 서버 RelayCtl.meta(phase · current · paused · stages[] · timers · mine · myStage).
 * mine 이 없으면(P3-S1 룸) myStage 와 stages 로 만든다.
 */
export function readRelayMeta(mode: Record<string, unknown>, now = 0): RelayView {
  const info = arr(mode.stages)
    .map(obj)
    .filter((s): s is Record<string, unknown> => !!s)
    .sort((a, b) => (num(a.stage) ?? 0) - (num(b.stage) ?? 0))
    .map(readInfo);
  const my = num(mode.myStage) ?? num(obj(mode.mine)?.stage);
  const myStage = my !== null && my >= 0 && my < info.length ? my : null;
  const mine = readMine(mode.mine, info, myStage);
  return {
    phase: readPhase(mode.phase ?? mode.state),
    current: num(mode.current),
    paused: mode.paused === true,
    stages: info.map((s) => s.status),
    info,
    myStage: mine?.stage ?? null,
    myFail: mine?.lastFail ?? null,
    mine,
    timers: readTimers(mode.timers),
    timersAt: now,
  };
}

/** 카운트다운 남은 초(정수 올림) — 일시정지면 받은 값 그대로 · 없으면 null */
export function leftS(v: RelayView, key: keyof RelayTimers, now: number): number | null {
  const base = v.timers[key];
  if (base === undefined) return null;
  const used = v.paused ? 0 : Math.max(0, now - v.timersAt) / 1000;
  return Math.max(0, Math.ceil(base - used));
}

/** ev relayStage { ev: RelayEvent } 하나를 반영 — 모르는 이벤트는 그대로 */
export function applyRelayEvent(v: RelayView, raw: unknown, now = 0): RelayView {
  const e = obj(raw);
  if (!e) return v;
  const kind = e.kind;
  const stage = num(e.stage);
  const setInfo = (i: number, st: RelayStageStatus): RelayStageInfo[] => {
    const xs = [...v.info];
    while (xs.length <= i)
      xs.push({
        status: "waiting",
        machine: null,
        in: "ball",
        out: "ball",
        ready: false,
        timeoutS: null,
      });
    xs[i] = { ...xs[i]!, status: st };
    return xs;
  };
  const withStage = (
    i: number,
    st: RelayStageStatus,
    extra: Partial<RelayView> = {},
  ): RelayView => {
    const info = setInfo(i, st);
    const mine = v.mine && v.mine.stage === i ? { ...v.mine, status: st } : v.mine;
    return { ...v, info, stages: info.map((s) => s.status), mine, ...extra };
  };
  const attempt = num(e.attempt);
  const isMine = stage !== null && stage === v.myStage;
  switch (kind) {
    case "started": {
      const info = v.info.map((s) => ({ ...s, status: "waiting" as const }));
      return {
        ...v,
        phase: "running",
        current: 0,
        paused: false,
        info,
        stages: info.map((s) => s.status),
        mine: v.mine ? { ...v.mine, status: "waiting", lastFail: null } : null,
        myFail: null,
        timers: {},
        timersAt: now,
      };
    }
    case "armed":
    case "running": {
      if (stage === null) return v;
      const run = kind === "running";
      const tS = v.info[stage]?.timeoutS ?? null;
      const next = withStage(stage, kind, {
        phase: "running",
        current: stage,
        myFail: isMine ? null : v.myFail,
        timers: run ? (tS !== null ? { runLeftS: tS } : {}) : { armLeftS: RELAY_ARM_S },
        timersAt: now,
      });
      if (isMine && next.mine)
        next.mine = {
          ...next.mine,
          attempt: attempt ?? next.mine.attempt,
          lastFail: null,
          // 미룬 코드는 다음 Armed 에 적용된다(DEC-003 §9)
          swapPending: kind === "armed" ? false : next.mine.swapPending,
        };
      return next;
    }
    case "retry":
      return stage === null
        ? v
        : {
            ...v,
            myFail: isMine ? null : v.myFail,
            mine: isMine && v.mine ? { ...v.mine, lastFail: null } : v.mine,
          };
    case "done":
    case "skipped":
      return stage === null
        ? v
        : withStage(stage, kind, stage === v.current ? { timers: {}, timersAt: now } : {});
    case "failed": {
      if (stage === null) return v;
      const reason = readFail(e.reason);
      const next = withStage(stage, "failed", {
        current: stage,
        myFail: isMine && reason ? reason : v.myFail,
        timers: { retryInS: RELAY_AUTO_RETRY_S },
        timersAt: now,
      });
      if (isMine && next.mine)
        next.mine = {
          ...next.mine,
          lastFail: reason ?? next.mine.lastFail,
          attempt: attempt ?? next.mine.attempt,
        };
      return next;
    }
    case "paused":
    case "resumed": {
      // 멈추는 순간의 남은 값으로 다시 센다(일시정지 동안 시간이 멈춘다 — DEC-003 §2-4)
      const timers: RelayTimers = {};
      for (const k of Object.keys(v.timers) as (keyof RelayTimers)[]) {
        const base = v.timers[k]!;
        timers[k] = v.paused ? base : Math.max(0, base - Math.max(0, now - v.timersAt) / 1000);
      }
      return { ...v, paused: kind === "paused", timers, timersAt: now };
    }
    case "allDone":
      return { ...v, phase: "allDone", current: null, paused: false, timers: {}, timersAt: now };
    default:
      return v;
  }
}

/** 내 구간 '다 됐어'(Setup 확정) 표시 — 낙관적으로 바꾸고 거절(ev relayRejected)이면 되돌린다 */
export function setMyReady(v: RelayView, ready: boolean): RelayView {
  if (!v.mine) return v;
  const info = v.info.map((s, i) => (i === v.mine!.stage ? { ...s, ready } : s));
  return { ...v, info, mine: { ...v.mine, ready } };
}

/** 진행 중에 가져온 코드(200 deferred) — 다음 시도에 적용 */
export function setSwapPending(v: RelayView, pending: boolean): RelayView {
  return v.mine ? { ...v, mine: { ...v.mine, swapPending: pending } } : v;
}

export function studentRelayState(v: RelayView): StudentRelayState {
  if (v.phase === "allDone") return "allDone";
  const m = v.mine;
  if (!m) return v.phase === "setup" ? "setupUnplaced" : "cheer";
  if (v.phase === "setup") return m.ready ? "ready" : "preparing";
  const st = v.stages[m.stage] ?? m.status;
  if (st === "done") return "done";
  if (st === "skipped") return "skipped";
  if (v.current === m.stage) {
    if (st === "failed") return "retry";
    if (st === "armed" || st === "running") return "myTurn";
  }
  return "waitTurn";
}

export function relayActions(s: StudentRelayState): RelayActions {
  switch (s) {
    case "preparing":
    case "ready":
      return { fix: true, fetch: "now", test: true, done: "toggle" };
    case "waitTurn":
      return { fix: true, fetch: "now", test: true, done: false };
    case "myTurn":
      return { fix: true, fetch: "defer", test: false, done: false };
    case "retry":
      return { fix: true, fetch: "now", test: true, done: "retry" };
    default:
      return { fix: false, fetch: false, test: false, done: false };
  }
}

export function relayNow(v: RelayView): RelayNowKind {
  if (v.phase === "allDone") return "allDone";
  if (v.phase === "setup") return "setup";
  if (v.paused) return "paused";
  return v.current !== null && v.stages[v.current] === "failed" ? "retrying" : "running";
}

export function relayNotice(v: RelayView): RelayNoticeKind {
  if (v.phase === "allDone") return "allDone";
  if (v.phase === "setup") return v.myStage !== null ? "setupMine" : "setup";
  const cur = v.current;
  const mine = v.myStage;
  if (mine !== null) {
    const st = v.stages[mine];
    if (cur === mine && st === "failed") return "myRetry";
    if (cur === mine && (st === "armed" || st === "running")) return "myTurn";
    if (st === "done") return "myDone";
    if (st === "skipped") return "mySkipped";
  }
  if (mine === null) return "cheer";
  if (cur !== null && v.stages[cur] === "failed") return "retrying";
  return "running";
}
